import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, MessageCircle, Home, Sparkles } from 'lucide-react';
import profile1 from '../../assets/1.jpg';
import profile2 from '../../assets/2.jpg';
import profile3 from '../../assets/3.jpg';
import profile4 from '../../assets/4.jpg';
import profile5 from '../../assets/5.jpg';

export const CommunityBlock = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const features = [
    {
      icon: Users,
      title: 'קהילה משלכם',
      description: 'התחברו לשכנים ולעסקים באזורכם',
    },
    {
      icon: MessageCircle,
      title: 'קהילה תומכת',
      description: 'צרו קהילות ותמכו אחד בשני',
    },
    {
      icon: Home,
      title: 'שייכות מקומית',
      description: 'הרגישו חלק מהשכונה שלכם',
    },
  ];

  return (
    <section ref={ref} className="relative py-12 sm:py-16 px-6 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 overflow-hidden">

      {/* Animated background elements */}
      <motion.div
        className="absolute top-20 right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Title section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="inline-block mb-4"
          >
            <div className="relative">
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-white/10 rounded-full blur-xl"
              />
              <div className="relative bg-white/20 backdrop-blur-sm p-3 sm:p-3.5 md:p-4 rounded-full">
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" fill="currentColor" strokeWidth={1.5} />
              </div>
            </div>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-3">
            זה שלכם.
            <br />
            תבנו את הקהילה.
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-white/95 font-medium max-w-3xl mx-auto"
          >
            קהילה חזקה נבנית על ידי אנשים כמוכם, שמשתפים ותומכים אחד בשני
          </motion.p>
        </motion.div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.15 }}
              whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
              className="group"
            >
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-4 sm:p-5 md:p-6 border border-white/20 h-full hover:bg-white/15 transition-all duration-300 shadow-xl">
                {/* Icon */}
                <div className="mb-6">
                  <div className="relative inline-block">
                    <div className="absolute inset-0 bg-white rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
                    <div className="relative bg-white/20 backdrop-blur-sm p-3 sm:p-3.5 md:p-4 rounded-2xl group-hover:bg-white/30 transition-colors">
                      <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-base sm:text-base md:text-lg text-white/90 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Inspirational visual - people connecting */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="relative"
        >
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-10 border border-white/20">
            <div className="flex flex-wrap justify-center items-center gap-12">
              {/* Central community icon */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <div className="absolute inset-0 bg-white rounded-full blur-2xl opacity-30" />
                <div className="relative bg-gradient-to-br from-white to-white/80 p-5 sm:p-6 md:p-8 rounded-full shadow-2xl">
                  <Users className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 text-brand-400" strokeWidth={1.5} />
                </div>
              </motion.div>

              {/* Connecting profile images around */}
              <div className="flex flex-wrap justify-center gap-6">
                {[profile1, profile2, profile3, profile4, profile5].map((profileImg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={isInView ? {
                      opacity: 1,
                      scale: 1,
                    } : {}}
                    transition={{
                      duration: 0.5,
                      delay: 1 + i * 0.1,
                    }}
                    className="w-16 h-16 rounded-full border-2 border-white/40 overflow-hidden"
                  >
                    <img
                      src={profileImg}
                      alt={`Community member ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.5 }}
              className="mt-8 text-center"
            >
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white leading-relaxed">
                "כשאנחנו מתחברים, אנחנו יוצרים משהו גדול יותר מעצמנו"
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
